import { useState } from 'react'
import { Info, Plus } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useBanks, useSettingsMutations } from '../api'

/** The bank list, and the routing codes a wage-protection file needs. */
export function BankMasterPage() {
  useBreadcrumb()
  const banks = useBanks()
  const actions = useSettingsMutations()
  const [draft, setDraft] = useState<{
    id?: string; code: string; name: string; shortName: string; swiftCode: string
    routingCode: string; countryCode: string; active: boolean
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Banks"
        subtitle="Where salaries go, and how a transfer is routed."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                code: '', name: '', shortName: '', swiftCode: '', routingCode: '',
                countryCode: '', active: true,
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New bank
          </Button>
        }
      />

      <p className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Nothing is seeded. Routing codes are issued by a central bank and they change; a wrong
        one sends somebody's salary nowhere, which is not a mistake a product should make on an
        employer's behalf.
      </p>

      <Card>
        {banks.isPending && <Skeleton className="m-4 h-20" />}
        {banks.isError && <ErrorState error={banks.error} onRetry={() => void banks.refetch()} />}
        {banks.isSuccess && banks.data.length === 0 && (
          <EmptyState
            title="No bank on file"
            description="Add the banks your people are paid through. A wage-protection file needs each one's routing code."
          />
        )}
        {banks.isSuccess && banks.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
              <tr>
                <th className="p-3 text-start font-medium">Code</th>
                <th className="p-3 text-start font-medium">Bank</th>
                <th className="p-3 text-start font-medium">SWIFT</th>
                <th className="p-3 text-start font-medium">Routing</th>
                <th className="p-3 text-start font-medium">Country</th>
                <th className="p-3 text-start font-medium">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {banks.data.map((bank) => (
                <tr key={bank.id} className="border-b border-[var(--color-border)]">
                  <td className="p-3 font-mono">{bank.code}</td>
                  <td className="p-3">{bank.name}</td>
                  <td className="p-3">{bank.swiftCode ?? '—'}</td>
                  <td className="p-3">
                    {bank.routingCode ?? (
                      <span className="text-[var(--color-muted)]" title="A wage-protection file cannot route to this bank without one.">
                        Not set
                      </span>
                    )}
                  </td>
                  <td className="p-3">{bank.countryCode ?? '—'}</td>
                  <td className="p-3">
                    <StatusBadge
                      status={bank.active ? 'ACTIVE' : 'INACTIVE'}
                      tone={bank.active ? 'success' : 'neutral'}
                      label={bank.active ? 'Active' : 'Off'}
                    />
                  </td>
                  <td className="p-3 text-end">
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => {
                        setError(null)
                        setDraft({
                          id: bank.id, code: bank.code, name: bank.name,
                          shortName: bank.shortName ?? '', swiftCode: bank.swiftCode ?? '',
                          routingCode: bank.routingCode ?? '',
                          countryCode: bank.countryCode ?? '', active: bank.active,
                        })
                      }}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setError(null) } }}
        title={draft?.id ? 'Edit bank' : 'New bank'}
        submitLabel="Save"
        loading={actions.saveBank.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await actions.saveBank.mutateAsync({
                id: draft.id,
                code: draft.code,
                name: draft.name,
                shortName: draft.shortName || null,
                swiftCode: draft.swiftCode || null,
                routingCode: draft.routingCode || null,
                countryCode: draft.countryCode || null,
                active: draft.active,
                note: null,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That bank could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="bank-code">Code</Label>
              <Input
                id="bank-code" value={draft.code}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="bank-name">Name</Label>
              <Input
                id="bank-name" value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="bank-swift">SWIFT</Label>
              <Input
                id="bank-swift" value={draft.swiftCode}
                onChange={(event) => setDraft({ ...draft, swiftCode: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="bank-routing">Routing code</Label>
              <Input
                id="bank-routing" value={draft.routingCode}
                onChange={(event) => setDraft({ ...draft, routingCode: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                What a wage-protection file uses to route the transfer.
              </p>
            </div>
            <div>
              <Label htmlFor="bank-country">Country</Label>
              <Input
                id="bank-country" value={draft.countryCode} placeholder="AE"
                onChange={(event) => setDraft({ ...draft, countryCode: event.target.value })}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox" className="size-4" checked={draft.active}
                  onChange={(event) => setDraft({ ...draft, active: event.target.checked })}
                />
                Offered when choosing a bank
              </label>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
